import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import OperatorCredentials from './OperatorCredentials';
import VerificationChecklist from './VerificationChecklist';
import HometobSyncPanel from './HometobSyncPanel';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Calendar, 
  FileText, 
  Building, 
  TrendingUp, 
  Users, 
  AlertTriangle,
  Euro,
  CheckCircle,
  FileDown,
  Loader2,
  BookOpen,
  ExternalLink
} from 'lucide-react';

const CATALOG_META = {
  welfare: { label: 'Welfare aziendale', path: '/welfare/' },
  companyprofile: { label: 'Company Profile (proprietari)', path: '/companyprofile/' },
  partner: { label: 'Partnership', path: '/partnership/' },
  'companyprofile-eventi': { label: 'Company Profile', path: '/company-profiles', hub: true },
};

const AdminDashboard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [ceReportLoading, setCeReportLoading] = useState(false);

  const downloadCasaEugenioReport = async () => {
    setCeReportLoading(true);
    try {
      const res = await api.get('/admin/report/casa-eugenio.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'report_casa_di_eugenio.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report Casa di Eugenio scaricato');
    } catch (err) {
      toast.error('Errore nel download del report');
    } finally {
      setCeReportLoading(false);
    }
  };
  const [catalogStats, setCatalogStats] = useState(null);

  const downloadProgressReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get('/admin/report/preview.pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_avanzamento_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report di avanzamento scaricato');
    } catch (err) {
      toast.error('Errore nel download del report');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIData();
    api.get('/admin/catalogs/stats', { timeout: 10000 })
      .then((res) => setCatalogStats(res.data))
      .catch(() => setCatalogStats(null));
  }, []);

  const fetchKPIData = async () => {
    try {
      const response = await api.get('/kpi/overview', { timeout: 15000 });
      setKpiData(response.data);
    } catch (error) {
      console.warn('KPI data fetch timeout/error, using cached or default values');
      // Don't block the dashboard - show with empty data
      setKpiData({
        total_bookings: '-',
        confirmed_bookings: '-',
        total_revenue: 0,
        pending_invoices: '-',
        overdue_invoices: '-',
        active_properties: '-'
      });
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      title: 'Prenotazioni Totali',
      value: kpiData?.total_bookings || 0,
      icon: Calendar,
      color: 'from-blue-500 to-blue-600',
      description: `${kpiData?.confirmed_bookings || 0} confermate`
    },
    {
      title: 'Fatturato Totale',
      value: `€${(kpiData?.total_revenue || 0).toLocaleString('it-IT')}`,
      icon: Euro,
      color: 'from-emerald-500 to-emerald-600',
      description: 'Da prenotazioni confermate'
    },
    {
      title: 'Fatture in Sospeso',
      value: kpiData?.pending_invoices || 0,
      icon: FileText,
      color: 'from-amber-500 to-amber-600',
      description: `${kpiData?.overdue_invoices || 0} scadute`
    },
    {
      title: 'Immobili Attivi',
      value: kpiData?.active_properties || 0,
      icon: Building,
      color: 'from-purple-500 to-purple-600',
      description: 'Disponibili per prenotazioni'
    }
  ];

  if (loading) {
    return (
      <Layout title="Dashboard Amministratore">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard Amministratore">
      <div className="space-y-8 fade-in">
        {/* Welcome section */}
        <Card className="p-6 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white border-0 shadow-xl">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Benvenuto in HWsmart</h2>
              <p className="text-blue-100">Panoramica completa del tuo business immobiliare</p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:items-center">
              <Button
                variant="outline"
                onClick={downloadProgressReport}
                disabled={reportLoading}
                className="h-auto w-full whitespace-normal bg-white/10 py-2 text-left text-white border-white/30 hover:bg-white/20 hover:text-white sm:w-auto"
                data-testid="download-progress-report-btn"
              >
                {reportLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                Report avanzamento (PDF)
              </Button>
              <Button
                variant="outline"
                onClick={downloadCasaEugenioReport}
                disabled={ceReportLoading}
                className="h-auto w-full whitespace-normal bg-white/10 py-2 text-left text-white border-white/30 hover:bg-white/20 hover:text-white sm:w-auto"
                data-testid="download-casa-eugenio-report-btn"
              >
                {ceReportLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                Report Casa di Eugenio (PDF)
              </Button>
              <TrendingUp className="h-16 w-16 text-blue-200 hidden lg:block" />
            </div>
          </div>
        </Card>

        {/* Cose da controllare */}
        <VerificationChecklist />

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="p-6 card-hover border-0 shadow-lg bg-white/90 backdrop-blur-sm" data-testid={`kpi-card-${index}`}>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-600">{card.title}</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid={`kpi-value-${index}`}>{card.value}</p>
                    <p className="text-xs text-slate-500">{card.description}</p>
                  </div>
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${card.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Cataloghi digitali — visite */}
        {catalogStats && (
          <Card className="p-6 border-0 shadow-lg bg-white/90 backdrop-blur-sm" data-testid="catalog-stats-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-900 to-blue-700">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Cataloghi digitali</h3>
                <p className="text-xs text-slate-500">Visite ai flipbook condivisi via mail e WhatsApp</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(CATALOG_META).map(([key, meta]) => {
                const s = catalogStats[key] || {};
                return (
                  <div key={key} className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid={`catalog-stat-${key}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-700">{meta.label}</p>
                      <a href={meta.path} target="_blank" rel="noreferrer" className="text-blue-800 hover:text-blue-600" data-testid={`catalog-open-${key}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                    {meta.hub ? (
                      <Button
                        size="sm"
                        className="mt-2 w-full bg-blue-900 text-xs hover:bg-blue-800"
                        data-testid="company-profiles-hub-button"
                        onClick={() => window.location.assign(meta.path)}
                      >
                        Apri raccolta
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs"
                        data-testid={`catalog-copy-${key}`}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}${meta.path}`);
                          toast.success(`Link ${meta.label} copiato! Incollalo in mail o WhatsApp`);
                        }}
                      >
                        Copia link da inviare
                      </Button>
                    )}
                    {meta.pdf && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs"
                        data-testid={`catalog-pdf-${key}`}
                        onClick={() => window.open(meta.pdf, '_blank')}
                      >
                        Scarica PDF
                      </Button>
                    )}
                    {meta.hub ? (
                      <p className="mt-3 text-sm font-semibold text-blue-900" data-testid="company-profiles-approved-count">6 profili disponibili</p>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-slate-900 mt-2" data-testid={`catalog-total-${key}`}>{s.total ?? 0}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {s.last7 ?? 0} negli ultimi 7 giorni
                          {s.last_visit ? ` · ultima: ${new Date(s.last_visit).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ' · nessuna visita'}
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Sincronizzazione HomeToB */}
        <HometobSyncPanel />

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="p-6 card-hover border-0 shadow-lg">
            <div className="text-center space-y-4">
              <Users className="h-12 w-12 mx-auto text-blue-900" />
              <h3 className="text-lg font-semibold">Gestione Utenti</h3>
              <p className="text-sm text-slate-600">Crea e gestisci account collaboratori</p>
              <Button 
                className="w-full bg-blue-900 hover:bg-blue-800"
                onClick={() => window.location.href = '/users'}
                data-testid="manage-users-btn"
              >
                Gestisci Utenti
              </Button>
            </div>
          </Card>

          <Card className="p-6 card-hover border-0 shadow-lg">
            <div className="text-center space-y-4">
              <CheckCircle className="h-12 w-12 mx-auto text-emerald-600" />
              <h3 className="text-lg font-semibold">Approva Fatture</h3>
              <p className="text-sm text-slate-600">Rivedi e approva fatture in sospeso</p>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => window.location.href = '/invoices'}
                data-testid="approve-invoices-btn"
              >
                Vai alle Fatture
              </Button>
            </div>
          </Card>

          <Card className="p-6 card-hover border-0 shadow-lg">
            <div className="text-center space-y-4">
              <AlertTriangle className="h-12 w-12 mx-auto text-amber-600" />
              <h3 className="text-lg font-semibold">Report & Analytics</h3>
              <p className="text-sm text-slate-600">Visualizza report dettagliati</p>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={() => alert('Funzionalità in arrivo!')}
                data-testid="view-reports-btn"
              >
                Visualizza Report
              </Button>
            </div>
          </Card>
        </div>

        {/* Operator Credentials */}
        <OperatorCredentials />

        {/* System Status */}
        <Card className="p-6 border-0 shadow-lg">
          <h3 className="text-lg font-semibold mb-4">Stato del Sistema</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Database Online</span>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">API Funzionante</span>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Backup Aggiornato</span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;